import { FilterMapper } from "@app/routing/mappers/FilterMapper";
import { IExtendedFilterValue } from "@app/interfaces/dtos/IExtendedFilterValue";
import { FilterType } from "@studyportals/omnisearch-interfaces";
import { RouteLocation } from "vue-router";
import { IExtendedWithPortalTypeFilterValue } from "@app/routing/mappers/IExtendedWithPortalTypeFilterValue";
import ISearchQueryFilter from "@domain/interfaces/dtos/ISearchQueryFilter";
import { ISearchResult } from "@domain/interfaces/dtos/ISearchResult";
import { IOrganisationIdAndName } from "@studyportals/search-api-interface";
import {useAPIBaseUrl} from "@composables/useAPIBaseUrl";

export class OrganisationFilterMapper extends FilterMapper {
	public filterType: FilterType = FilterType.ORGANISATIONS;
	public isMultiSelect = false; // TODO: set to true when multiple organisations in query param are supported
	private static instance: OrganisationFilterMapper;

	protected values: IExtendedWithPortalTypeFilterValue[] = [];

	public static getInstance(): OrganisationFilterMapper {
		if (!OrganisationFilterMapper.instance) {
			OrganisationFilterMapper.instance = new OrganisationFilterMapper();
		}
		return OrganisationFilterMapper.instance;
	}

	private constructor() {
		super();
	}
	public canBeIndexed(): boolean {
		return false;
	}

	private async fetchOrganisations(filters: ISearchQueryFilter[], virtualPath?: string): Promise<ISearchResult<IOrganisationIdAndName>> {
		return await $fetch('/api/search/filters/organisations', {
			baseURL: useAPIBaseUrl(),
			method: 'POST',
			body: { filters, virtualPath }
		});
	}

	public override async getExtendedFilterValuesFromRoute(route: RouteLocation): Promise<IExtendedFilterValue[]> {
		const organisationValues: IExtendedFilterValue[] = [];

		const paramValue = await this.retrieveOrganisationDataFromParam(route);
		if (paramValue) organisationValues.push(paramValue);

		const queryValues = await this.retrieveOrganisationDataFromQuery(route);
		if (queryValues) organisationValues.push(...queryValues);

		return organisationValues;
	}

	public override getAllExtendedFilterValues(): IExtendedFilterValue[] {
		throw new Error('Not supported for OrganisationFilterMapper');
	}

	public async getExtendedFilterValueByIdWithFetch(id: string): Promise<IExtendedFilterValue | undefined> {
		const existingValue = this.getExtendedFilterValueById(id);
		if (existingValue) return existingValue;

		const results = await this.fetchOrganisations([{ type: FilterType.ORGANISATIONS, values: [id] }]);
		if (results.results.length === 0) return;

		const result = results.results[0]!;
		const value = {
			id: result.organisationId.toString(),
			name: result.organisationName,
			virtualPath: result.virtualPath
		};

		this.values.push(value);
		return value;
	}

	private async retrieveOrganisationDataFromParam(route: RouteLocation): Promise<IExtendedFilterValue | undefined> {
		const paramValue = route.params?.[this.filterType] ?? route.params?.[FilterType.CITY];
		if (paramValue === undefined) return;

		const virtualPath = String(paramValue);
		const existingValue = this.values.find(v => v.virtualPath === virtualPath);
		if(existingValue) return existingValue;

		const results = await this.fetchOrganisations([], virtualPath);
		if(results.results.length === 0) return;

		const result = results.results[0]!;

		const value = {
			id: result.organisationId.toString(),
			name: result.organisationName,
			virtualPath: result.virtualPath
		}

		this.values.push(value);
		return value;
	}

	private async retrieveOrganisationDataFromQuery(route: RouteLocation): Promise<IExtendedWithPortalTypeFilterValue[]> {
		const values: IExtendedFilterValue[] = [];

		if (route.query?.[this.filterType] === undefined) return [];
		const allIds = (route.query[this.filterType] as string).split(',');
		const ids = [];

		for(const id of allIds) {
			const value = this.values.find(v => v.id === id);
			if(value) {
				values.push(value);
			} else {
				ids.push(id);
			}
		}

		if(ids.length === 0) return values;

		const results = await this.fetchOrganisations([{type: FilterType.ORGANISATIONS, values: ids}]);
		for(const result of results.results) {

			const value = {
				id: result.organisationId.toString(),
				name: result.organisationName,
				virtualPath: result.virtualPath
			};

			values.push(value);
		}

		this.values.push(...values);
		return values;
	}
}
